import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSites } from '@/hooks/useSites'
import { useMapFiles } from '@/hooks/useMapFiles'
import SiteSelector from '@/components/map/SiteSelector'
import FileList from '@/components/map/FileList'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { AlertCircle, Database } from 'lucide-react'

export default function HomePage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSiteId = searchParams.get('site')
  const selectedRobotId = searchParams.get('robot')

  const handleSelectSite = useCallback(
    (siteId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (siteId) next.set('site', siteId)
          else next.delete('site')
          next.delete('robot')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const handleSelectRobot = useCallback(
    (robotId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (robotId) next.set('robot', robotId)
          else next.delete('robot')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { data: sites, isLoading: sitesLoading, error: sitesError } = useSites()

  const {
    data: mapFilesData,
    isLoading: filesLoading,
    error: filesError,
  } = useMapFiles(selectedSiteId, selectedRobotId)

  return (
    <div className="h-full flex flex-col">
      {sitesLoading ? (
        <div className="flex-1 flex flex-col justify-center items-center gap-3">
          <LoadingSpinner size="lg" />
          <p className="text-sm text-gray-500">{t('sites.loading')}</p>
        </div>
      ) : sitesError ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {sitesError.message}
        </div>
      ) : sites && sites.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <Database className="w-12 h-12 mb-3 text-gray-300" />
          <p>{t('sites.noSites')}</p>
        </div>
      ) : sites ? (
        <div className="flex-1 min-h-0 bg-white border-t border-gray-200 flex overflow-hidden">
          {/* Sites & Forks columns */}
          <SiteSelector
            sites={sites}
            selectedSiteId={selectedSiteId}
            selectedRobotId={selectedRobotId}
            onSelectSite={handleSelectSite}
            onSelectRobot={handleSelectRobot}
          />

          {/* Files column */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {!selectedSiteId || !selectedRobotId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <Database className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm">{t('files.selectSiteAndFork')}</p>
              </div>
            ) : filesError ? (
              <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {filesError.message}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4">
                <FileList
                  files={mapFilesData?.files ?? []}
                  siteId={selectedSiteId}
                  robotId={selectedRobotId}
                  isLoading={filesLoading}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
